import { ControlPoint } from "@app/project/model/ch-controlpoint";
import { CHDevice } from "@app/project/model/ch-device";
import { CHService } from "@app/project/model/ch-service";
import { ServiceMetadataService } from "@app/project/services/service-metadata.service";
import {
    CHServiceMetadata,
    CHServiceMetadataState,
    ControlPointActionMetadata,
    initialServiceMetadataState,
} from "@app/project/store/clarehome.state";
import * as CHServiceSelectors from "@app/project/store/selectors/ch-services.selectors";
import * as CHDeviceSelectors from "@app/project/store/selectors/devices.selectors";
import * as ProjectSelectors from "@app/project/store/selectors/projects.selectors";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { LocalizerService } from "@app/shared/services/localization/localizer.service";
import { createFeatureSelector, createSelector } from "@ngrx/store";

const selectState = createFeatureSelector<CHServiceMetadataState>("chservicemetadata");

export const selectChServiceMetadataState = createSelector(
    selectState,
    ProjectSelectors.getUuid,
    (state: CHServiceMetadataState, uuid: string): CHServiceMetadataState => (state?.uuid ? state : initialServiceMetadataState(uuid))
);

export const selectChServiceMetadata = createSelector(selectChServiceMetadataState, (metadata: any) => metadata?.result || {});

const _getChServicesFromMetadata = (metadata, services) => {
    const servicesFromMetadata: ClareHomeDbCollection<CHService> = {};
    const serviceDefBlacklist = ["com.clarecontrols.widgets.audio.destination", "com.clarecontrols.widgets.audio.source"];

    if (metadata?.services && services?.result) {
        for (const instanceId in services.result) {
            if (services.result[instanceId]) {
                const service = services.result[instanceId];
                const serviceDefId = service.definitionId;
                if (
                    !serviceDefBlacklist.includes(serviceDefId) && // Temporary. New actions will use audio destination
                    Object.keys(metadata.services).includes(serviceDefId)
                ) {
                    servicesFromMetadata[instanceId] = service;
                }
            }
        }
    }
    return servicesFromMetadata;
};

// Gets a list of services that can be used in rules
export const getChServicesFromMetadata = createSelector(
    selectChServiceMetadata,
    CHServiceSelectors.selectChServicesState,
    (metadata: any, services: any) => _getChServicesFromMetadata(metadata, services)
);

// TODO: Remove this. This should not be a selector if all the params are supplied.
// Remove or supply metadata by using a metadata selector.
export const getAllControlPointsFromMetadata = (definitionId: string, metadata: CHServiceMetadata) => {
    let controlPoints = {};
    if (metadata?.services?.[definitionId]) {
        controlPoints = metadata.services[definitionId].controlPoints;
    }
    return controlPoints;
};

const _sortArray = (services: any[]): any[] => {
    if (services) {
        return services.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return [];
};

const _sortServiceActions = (services: any[]): any[] => {
    if (services) {
        return services.sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
    }
    return [];
};

const _getChServiceCategoriesList = (services, metadata) => {
    const categoryList = [];

    if (services && metadata) {
        Object.keys(services).forEach(instanceId => {
            const service = services[instanceId];
            const metaControlPoints = getAllControlPointsFromMetadata(service.definitionId, metadata);
            const controlPtKeys = Object.keys(service.controlPoints);

            if (controlPtKeys.length !== 0) {
                // if the services contain control points from metadata
                let isWritableProperty = false;
                const addService = controlPtKeys.some(controlPtKey => {
                    // if the control point key is enumerated
                    const newKey = controlPtKey.match(/([a-zA-Z_]+)/g);
                    const tmpControlPoint = metaControlPoints[newKey[0]];
                    if (tmpControlPoint && tmpControlPoint.Properties) {
                        // check whether the property is writable
                        Object.values(tmpControlPoint.Properties).forEach((property: any) => {
                            if (property.Metadata.access.includes("W")) {
                                isWritableProperty = true;
                            }
                        });
                    }
                    // the key exists in metadata, and contains operations or writable properties
                    return metaControlPoints[newKey[0]] && (metaControlPoints[newKey[0]].Operations || isWritableProperty);
                });
                if (addService) {
                    // Get the first category in the categories array
                    const serviceCategory = service.categories[0];
                    // see if the category is in the categoriesList array
                    const categoryIndex = categoryList.findIndex(category => category.key === serviceCategory);
                    if (categoryIndex === -1) {
                        categoryList.push({
                            key: serviceCategory,
                            name: LocalizerService.getDeviceCategory(serviceCategory),
                            services: [service],
                        });
                    } else {
                        categoryList[categoryIndex].services.push(service);
                    }
                }
            }
        });

        categoryList.forEach(category => _sortArray(category.services));
        return _sortArray(categoryList);
    }
};

export const getChServiceCategoriesList = createSelector(getChServicesFromMetadata, selectChServiceMetadata, (services: any, metadata) =>
    _getChServiceCategoriesList(services, metadata)
);

export const getChServiceCategoriesListByUuid = createSelector(
    getChServicesFromMetadata,
    selectChServiceMetadata,
    (services: any, metadata) => _getChServiceCategoriesList(services, metadata)
);

export const selectServicesByCategory = (categoryName: string) =>
    createSelector(getChServiceCategoriesList, (categoryList: any) => {
        let servicesList = [];
        categoryList.forEach(category => {
            if (category?.name === categoryName) {
                servicesList = category.services;
            }
        });
        return _sortArray(servicesList);
    });

/**
 *  ListItems: [{
 *      name: "Access",
 *      ListItems: [{
 *          name: "When Lock is locked",
 *          ListItems: [{
 *              name: "Front Door",
 *              condition: "Property Change Condition (...)"
 *          }]
 *      }]
 *  }
 */
export const getConditionChServiceCategoriesList = createSelector(
    selectState,
    getChServicesFromMetadata,
    selectChServiceMetadata,
    (state, services: any, metadata) => {
        const categoryList = [];
        const categoryMap = new Map();

        if (services && metadata) {
            services.forEach(service => {
                categoryMap.set(service.categories[0], service);
            });

            categoryMap.forEach((value, key) => {
                categoryList.push({ name: key, whenClauses: categoryMap.get(value) });
            });
        }

        return _sortArray(categoryList);
    }
);
const _getChServiceControlPointMetadata = (
    services: ClareHomeDbCollection<CHService>,
    metadata,
    devices: ClareHomeDbCollection<CHDevice>,
    service: CHService
) => {
    if (metadata?.services && devices) {
        const metaControlPoints = getAllControlPointsFromMetadata(service.definitionId, metadata);
        const serviceControlPoints = services[service.instanceId].controlPoints;
        const serviceActions = [];

        // Loop over each service control point and only add properties and
        // operations if these control points also exist in the metadata
        Object.values(serviceControlPoints).forEach((serviceControlPt: ControlPoint) => {
            const [, cpName] = ServiceMetadataService.isEnumeratedCp(serviceControlPt.uid);

            const cpMetadataKey = cpName || serviceControlPt.uid;

            // does the control point exist in metadata
            if (metaControlPoints[cpMetadataKey]) {
                // merge the operations and properties
                const actions = Object.assign({}, metaControlPoints[cpMetadataKey].Operations, metaControlPoints[cpMetadataKey].Properties);

                const device = devices[serviceControlPt.deviceUid];

                // loop over the operations and properties
                Object.values(actions).forEach((action: ControlPointActionMetadata) => {
                    if (
                        ServiceMetadataService.isControllable(action.Metadata) &&
                        // if the control point is enumerated, create a new "instance" for each index
                        !ServiceMetadataService.isActionDeprecated(action, serviceControlPt, device) &&
                        ServiceMetadataService.isActionSupported(action, serviceControlPt, device)
                    ) {
                        serviceActions.push(
                            Object.assign(
                                {
                                    controlPointId: serviceControlPt.uid,
                                    displayName: getFriendlyName(device, action, serviceControlPt.uid),
                                },
                                action
                            )
                        );
                    }
                });
            }
        });

        return _sortServiceActions(serviceActions);
    }
};

const getFriendlyName = (device, action, controlPointId) => {
    if (ServiceMetadataService.isEnumeratedCp(controlPointId).length) {
        return `${device.Name} - ${action.Metadata.friendlyName}`;
    }
    return action.Metadata.friendlyName;
};

export const getChServiceControlPointMetadata = (service: CHService) =>
    createSelector(getChServicesFromMetadata, selectChServiceMetadata, CHDeviceSelectors.getChDevices, (services, metadata, devices) =>
        _getChServiceControlPointMetadata(services, metadata, devices, service)
    );

export const selectServiceMetadataCallState = createSelector(selectChServiceMetadataState, state => state.callState);
